import { tools } from '@halo/ui-chat-react';
import { MsgPayloadType } from '@halo/chat-sdk';
import { useDropFiles } from '@halo/hooks';
import { Box, Flex, Show, Text } from '@chakra-ui/react';
import type { RefObject } from 'react';
import type { SessionStoreInstance } from '@/store/client';
import {
  pickFileDialog,
  PickFileDialogSource,
  PickFileDialogName,
  filter,
} from '@/components/PickFileDialog';

export interface SessionFileDragProps {
  disabled?: boolean;
  disabledLabel?: string;
  sessionStoreInstance: SessionStoreInstance;
  sessionName: string;
  bodyRef: RefObject<HTMLDivElement | null>;
}

export interface SessionFileDragRef {
  handleSendFile: (source: PickFileDialogSource, files: File[]) => Promise<void>;
  isDragging: boolean;
}

export const SessionFileDrag = forwardRef<SessionFileDragRef, SessionFileDragProps>(
  function SessionFileDrag(
    { disabled, disabledLabel, sessionStoreInstance, sessionName, bodyRef },
    ref
  ) {
    const { t } = useTranslation();
    // pick file
    const handleSendFile = useCallback(
      async (source: PickFileDialogSource, files: File[] = []) => {
        if (disabled) return;

        const sessionStore = sessionStoreInstance.getState();
        // filter
        const _ = await filter(files, source);
        if (_.length === 0) {
          tools.toaster.error({
            title: t('tips.unsupportedFileFormat'),
          });
          return;
        }
        if (_.length !== files.length) {
          tools.toaster.error({
            title: t('tips.someFileFormatsAreNotSupportedAndWereSkipped'),
          });
        }
        // open modal
        const res: { files: File[] } = await pickFileDialog.open(PickFileDialogName, {
          files: _,
          source,
          sessionName: sessionStore?.session?.name || '',
        });
        await pickFileDialog.waitForExit(PickFileDialogName);
        // send file
        if (!res.files) return;
        await Promise.allSettled(
          res?.files.map(async (file) => {
            let payload_type: MsgPayloadType = MsgPayloadType.FILE;
            let saveInMemo: boolean = false;
            if (source === PickFileDialogSource.IMAGE) {
              if (file.type.includes('image')) {
                payload_type = MsgPayloadType.IMAGE;
                saveInMemo = true;
              }
              if (file.type.includes('video')) {
                payload_type = MsgPayloadType.FILE;
              }
            }
            await sessionStore.sendFileMessage(payload_type, file, saveInMemo);
          })
        );
      },
      [sessionStoreInstance, disabled, t]
    );

    const { isDragging } = useDropFiles({
      target: bodyRef,
      disabled,
      onFiles: (files) => {
        handleSendFile(PickFileDialogSource.IMAGE, files);
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        handleSendFile,
        isDragging,
      }),
      [handleSendFile, isDragging]
    );

    return (
      <Show when={isDragging}>
        <Box
          position={'absolute'}
          inset={0}
          border={'2px dashed'}
          borderColor={disabled ? 'border.line' : 'brand.100'}
          borderRadius="lg"
          zIndex={9999}
          background={'whiteAlpha.10'}
          backdropFilter={'blur(200px)'}
          mx={4}
          marginBlockStart={4}
          marginBlockEnd={'72px'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          {disabled ? (
            <Box pointerEvents={'none'}>
              <Text fontWeight={600} fontSize={'md'} color={'fg.subtle'} textAlign={'center'}>
                {t('tips.unableToSend')}
              </Text>
              <Text fontWeight={600} fontSize={'md'} color={'fg.subtle'} textAlign={'center'}>
                {disabledLabel}
              </Text>
            </Box>
          ) : (
            <Flex maxW="480px" pointerEvents={'none'}>
              <Text lineClamp="2" fontWeight={600} color={'fg.subtle'} fontSize={'md'}>
                {t('chat.sendFilesToSession', { name: sessionName })}
              </Text>
            </Flex>
          )}
        </Box>
      </Show>
    );
  }
);
